import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import { useState } from "react";
import { Page } from "react-pdf";
import { useHistory } from "react-router-dom";
import { Lecture } from "../../../controller";
import { SlideInteractions } from "../../../controller/SlideInteraction";
import Colors from "../../../utils/Colors";

import { EnumExpressionColors, EnumExpressions, EnumExpressionsNum } from "../../../utils/enum/EnumExpressions";
import Images from "../../../utils/Images";
import Routes from "../../../utils/Routes";
import UserRole from "../../../utils/UserRole";
import { useEffectOnce } from "../../../utils/Utils";
import { SlideStatisticStateProps } from "../../pages/teacher/statistics/SlideStatisticsService";
import AnswerIcon from "../icons/AnswerIcon";
import DiscussonIcon from "../icons/DiscussonIcon";
import QuestionIcon from "../icons/QuestionIcon";

const colorStat = [
  { id: 1, color: EnumExpressionColors[1] },
  { id: 2, color: "#FFE212" },
  { id: 3, color: EnumExpressionColors[3] },
  { id: 4, color: EnumExpressionColors[4] },
  { id: 5, color: EnumExpressionColors[5] },
];

interface StatsCardProps {
  pageNumber: number;
  courseId: number;
  lectureNum: number;
  showAll: boolean;
  numOfPages: number;
  showSlide?: boolean;
  noPresentation?: boolean;
  urlVideo?: string;
  statsForWholeLecture?: boolean;
}

interface IntervalType {
  intervalNum: number;
  startInSec: string;
  endInSec: string;
  expressions: Array<ExpressionType | undefined>;
}

interface ExpressionType {
  id: number;
  activatedAtSec: number;
  tag: string;
}

interface SlideInteractionsType {
  slideNum: string;
  startedAt: string;
  duritationSec: number;
  intervals: [IntervalType];
}

export default function StatsCard(props: StatsCardProps) {
  const { pageNumber, courseId, lectureNum, showAll, numOfPages, showSlide, noPresentation, urlVideo = "", statsForWholeLecture = false } = props;
  const [expressions, setExpressions] = useState<any>({});
  const [totalExpressions, setTotalExpressions] = useState<number>(1);
  const [numQuestions, setNumQuestions] = useState<number>(0);
  const [numAnswers, setNumAnswers] = useState<number>(0);
  const [numDiscussions, setNumDiscussions] = useState<number>(0);
  const baseClass = showAll ? "stats-card-show" : "stats-card";
  const history = useHistory();
  const { isTeacher } = UserRole();
  const [percentagePerExpression, setPercentagePerExpression] = useState<any>([]);
  const showOldStatsPercent = false;

  const makeBarChartData = (dataArray: any, type: string) => dataArray?.map((data: any) => data[type]);

  const makeDataObject = (newData: SlideInteractionsType) => {
    let tagData: Array<any> = [];
    newData?.intervals?.forEach((interval: IntervalType, index: number) => {
      let one: Array<ExpressionType> = [];
      let two: Array<ExpressionType> = [];
      let three: Array<ExpressionType> = [];
      let four: Array<ExpressionType> = [];
      let five: Array<ExpressionType> = [];
      interval?.expressions?.forEach((expression: any) => {
        if (expression?.tag === "1") {
          one.push(expression);
        }
        if (expression?.tag === "2") {
          two.push(expression);
        }
        if (expression?.tag === "3") {
          three.push(expression);
        }
        if (expression?.tag === "4") {
          four.push(expression);
        }
        if (expression?.tag === "5") {
          five.push(expression);
        }
      });
      tagData.push({ "1": one, "2": two, "3": three, "4": four, "5": five });
    });
    return tagData;
  };

  const getExporessionValues = (): string[] => Object.values(EnumExpressionsNum).map((value: number) => value.toString());

  const calculatePercentageNew = (data: SlideInteractionsType) => {
    let overallSum = 0;
    let sumByTagArray: any = getExporessionValues().map((tag: string) => {
      let tagArray = makeBarChartData(makeDataObject(data), tag);
      let sumByTag = 0;
      tagArray.forEach((exp: Array<ExpressionType>) => {
        sumByTag += exp.length;
        overallSum += exp.length;
      });
      return sumByTag;
    });

    let returnArray: any = sumByTagArray.map((sum: number) => {
      const percent = overallSum !== 0 ? Math.round((sum / overallSum) * 100) : 0;
      return percent;
    });
    setPercentagePerExpression(returnArray);
    return returnArray;
  };

  // by lectureNum
  const getSlideInteractions = () => {
    SlideInteractions.getSlideInteractions(courseId, lectureNum)
      .then(({ data }: any) => {
        const res: SlideInteractionsType = data.data;
        calculatePercentageNew(res);
      })
      .catch((err: any) => console.log(err));
  };

  const getSlideStats = () => {
    Lecture.getExpressions(courseId, lectureNum)
      .then((data: any) => {
        setExpressions(data.expressionMap);
        setTotalExpressions(data.totalExpressions);
        setNumQuestions(data.numQuestions ?? 0);
        setNumAnswers(data.numAnswers ?? 0);
        setNumDiscussions(data.numDiscussions ?? 0);
      })
      .catch((err: any) => console.log(err));
  };

  // by slideNum
  const getSlideInteractionsBySlideNum = () => {
    SlideInteractions.getSlideInteractionsBySlideNum(courseId, lectureNum, pageNumber)
      .then(({ data }: any) => {
        const res: SlideInteractionsType = data.data;
        calculatePercentageNew(res);
      })
      .catch((err: any) => console.log(err));
  };

  const getSlideStatsBySlideNum = () => {
    Lecture.getExpressionsForSlide(courseId, lectureNum, pageNumber)
      .then((data: any) => {
        setExpressions(data.expressionMap);
        setTotalExpressions(data.totalExpressions);
        setNumQuestions(data.numQuestions ?? 0);
        setNumAnswers(data.numAnswers ?? 0);
        setNumDiscussions(data.numDiscussions ?? 0);
      })
      .catch((err: any) => console.log(err));
  };

  useEffectOnce(() => {
    if (statsForWholeLecture) {
      getSlideInteractions();
      getSlideStats();
    } else {
      getSlideInteractionsBySlideNum();
      getSlideStatsBySlideNum();
    }
  });

  const getPercent = (expressions: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((expressions / total) * 100);
  };

  return (
    <Card
      className={`${baseClass}`}
      onClick={() => {
        const showNavigationButtons = !noPresentation && !urlVideo && !statsForWholeLecture;
        const locationState: SlideStatisticStateProps = { courseId, lectureNum, pageNumber, numOfPages, showNavigationButtons, statsForWholeLecture, urlVideo };
        isTeacher && history.push({ pathname: Routes.TEACHER_SLIDE_STATS, state: locationState });
      }}
    >
      <CardContent style={{ padding: 0, display: "flex", justifyContent: "center" }}>
        {noPresentation || statsForWholeLecture ? (
          <Grid container item>
            <Box component="img" sx={{ width: "100%", objectFit: "contain" }} alt="???" src={Images.NO_PRESENTATION} />
          </Grid>
        ) : urlVideo ? (
          <Grid container item>
            <Box component="img" sx={{ width: "100%", objectFit: "contain" }} alt="???" src={Images.VIDEO_INTERACTION} />
          </Grid>
        ) : isTeacher || showSlide ? (
          <Page pageNumber={pageNumber} height={180} />
        ) : (
          <Grid container item justifyContent="center" alignItems="center" style={{ minHeight: 180, width: "100%", backgroundColor: Colors.NAVY_SOLID, color: Colors.WHITE }}>
            <Grid item xs={12}>
              {`Slide No. ${pageNumber}`}
            </Grid>
          </Grid>
        )}

        {!urlVideo && (
          <Grid container className="interactions-container" flexDirection="row" justifyContent="space-between" alignItems="center">
            <Grid container item xs={3} justifyContent="center" alignItems="center">
              <QuestionIcon sideLength="12px" />
              <Typography fontWeight={500} fontSize="9px" ml="1px">
                {numQuestions}
              </Typography>
            </Grid>
            <Grid container item xs={3} justifyContent="center" alignItems="center">
              <AnswerIcon sideLength="12px" />
              <Typography fontWeight={500} fontSize="9px" ml="1px">
                {numAnswers}
              </Typography>
            </Grid>
            <Grid container item xs={3} justifyContent="center" alignItems="center">
              <DiscussonIcon sideLength="12px" />
              <Typography fontWeight={500} fontSize="9px" ml="1px">
                {numDiscussions}
              </Typography>
            </Grid>
          </Grid>
        )}
        <Grid container className="hover-container" justifyContent="space-around">
          <Grid container className="stats-container" flexDirection="row" justifyContent="center">
            {colorStat.map(({ id, color }) => {
              const newPercent = percentagePerExpression && percentagePerExpression[id - 1];
              const percent = showOldStatsPercent ? getPercent(expressions[id], totalExpressions) : newPercent;
              return <Grid key={id} item xs={(percent / 10) * 1.2} style={{ background: color }} />;
            })}
          </Grid>
          {colorStat.map(({ id, color }) => {
            const newPercent = percentagePerExpression && percentagePerExpression[id - 1] ? `${percentagePerExpression[id - 1]}%` : "0%";
            const oldPercentExists = expressions && Object.keys(expressions).length !== 0 && `${getPercent(expressions[id], totalExpressions)}%`;
            const percent = showOldStatsPercent ? oldPercentExists : newPercent;
            return (
              <Grid key={id} container flexDirection="column" item xs={2} spacing={1} p={1}>
                <Grid item style={{ color: color, fontSize: 12, fontWeight: 600 }}>
                  <Typography fontSize="10px" fontWeight="bold">
                    {percent}
                  </Typography>
                </Grid>
                <Grid item style={{ color: "white", fontSize: 9, fontWeight: 500 }}>
                  <Typography fontSize="5.5px" fontWeight="bold">
                    {EnumExpressions[id]}
                  </Typography>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
}
